import { ExclamationTriangleIcon, FlagIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';

import type { JSX, ReactElement } from 'react';

export interface BadgeProps {
  label: ReactElement | string;
  text?: string | JSX.Element;
  type: BadgeTypes;
  showIcon?: boolean;
  isCircular?: boolean;
  onButtonClick?: VoidFunction;
  className?: string;
}

export enum BadgeTypes {
  DANGER = 'danger',
  WARNING = 'warning',
  SUCCESS = 'success-light',
  PENDING = 'pending-light',
  SECONDARY = 'secondary',
  INFO = 'info',
}

export const Badge = (props: BadgeProps) => {
  const { type, onButtonClick, label, text, showIcon, isCircular, className } = props;

  function ConditionallyClickable(props: { children: React.ReactNode }) {
    if (!onButtonClick) return props.children;
    return <button onClick={onButtonClick}>{props.children}</button>;
  }

  const iconToShow = {
    [BadgeTypes.DANGER]: <FlagIcon className="text-fiona-danger h-4 w-4" />,
    [BadgeTypes.WARNING]: (
      <ExclamationTriangleIcon className="text-fiona-warning-contrast h-4 w-4" />
    ),
  }[type];

  return (
    <ConditionallyClickable>
      <span
        className={clsx(
          'inline-flex items-start gap-x-1.5 border px-2 py-1 text-xs',
          isCircular ? 'rounded-full' : 'rounded-md',
          {
            [BadgeTypes.DANGER]: 'border-fiona-danger-border text-fiona-danger-text bg-red-50',
            [BadgeTypes.WARNING]:
              'border-fiona-warning-border text-fiona-warning-text bg-yellow-50',
            [BadgeTypes.PENDING]: 'bg-purple-100 text-purple-800',
            [BadgeTypes.SUCCESS]: 'bg-green-100 text-green-800',
            [BadgeTypes.SECONDARY]: 'bg-gray-100 text-black',
            [BadgeTypes.INFO]: 'text-fiona-info bg-blue-100',
          }[type],
          text
            ? 'w-full' // detailed
            : 'font-medium', // light
          className,
        )}
      >
        {showIcon && iconToShow}
        <span
          className={clsx(
            'text-nowrap',
            text
              ? type === BadgeTypes.DANGER //detailed
                ? 'text-fiona-danger text-nowrap text-sm font-bold'
                : 'text-nowrap text-sm font-bold'
              : 'text-xs capitalize', //light
          )}
        >
          {label}
          {text && ':'}
        </span>
        {text && <p className="text-sm">{text}</p>}
      </span>
    </ConditionallyClickable>
  );
};
